import { TypedEvent, type GUID } from "@faro-lotv/foundation";
import { safeDispose } from "@faro-lotv/lotv";
import type { Object3D } from "three";

/**
 * An object that must be unique for each code module acquiring a cached object
 * Two objects with the same label are considered as two distinct acquirers.
 */
export type ObjectCacheAcquirer = {
  /** String label that identifies this acquirer */
  readonly label: string;
};

export type ObjectCacheAcquirers = ObjectCacheAcquirer[];

/**
 * An entry in an object cache
 */
export class CacheEntry<Type extends Object3D> {
  /** The keys to identify this entry in the cache */
  key: GUID;
  /** The promise loading the data */
  promise: Promise<Type>;
  /** Set of acquirers holding a reference to this cached object */
  #acquirers = new Set<ObjectCacheAcquirer>();
  /** How many milliseconds to keep this object in cache when refCount goes to 0 */
  lifespan = 0;
  /** The loading error if the loading failed */
  error?: unknown;
  /** The loaded object when the promise resolve */
  response?: Type;
  /** The timeout id for the disposal that start when refCount goes to 0 */
  disposeTimeout?: number;
  /** Signal this entry has been disposed */
  disposed = new TypedEvent<void>();
  /** Whether the promise was settled or not, regardless of fulfillment or rejection */
  #promiseSettled = false;

  /**
   * Create a new cache entry
   *
   * @param promise The promise loading the data
   * @param key The key to identify this entry in the cache
   * @param lifespan How many milliseconds to keep this alive when refcount goes to 0
   */
  constructor(promise: Promise<Type>, key: GUID, lifespan: number) {
    this.key = key;
    this.promise = promise;
    this.lifespan = lifespan;
    this.promise
      .then((value) => {
        this.response = value;
      })
      .catch((error) => {
        // It should be sufficient to remember only one error
        if (this.error === undefined) {
          this.error = error;
        }
      })
      .finally(() => {
        this.#promiseSettled = true;
        this.scheduleDisposeIfNeeded();
      });
  }

  /**
   * Get this object from the cache or suspend execution while it's loaded
   *
   * @returns The object or suspend
   */
  getOrSuspend(): Type {
    if (this.response) return this.response;
    if (this.error) throw this.error;
    throw this.promise;
  }

  /**
   * Sets this object as acquired by an owner
   *
   * @param owner The owner acquiring this object
   */
  acquire(owner: ObjectCacheAcquirer): void {
    if (this.#acquirers.has(owner)) return;
    this.#acquirers.add(owner);
    if (this.disposeTimeout) {
      this.dismissDispose();
    }
  }

  /**
   * Sets the object as released by the given owner. An object
   * with no owner will be deallocated after a given lifespan.
   *
   * @param owner The owner releasing this object
   */
  release(owner: ObjectCacheAcquirer): void {
    if (!this.#acquirers.has(owner)) return;
    this.#acquirers.delete(owner);
    this.scheduleDisposeIfNeeded();
  }

  /**
   * Transfers acquirers info to another cache entry. Useful when e.g.
   * reloading an object in the cache and creating a new entry for it.
   *
   * @param target Target cache entry to move acquirers to
   */
  moveAcquirersTo(target: CacheEntry<Type>): void {
    for (const acquirer of this.#acquirers) {
      target.acquire(acquirer);
    }
    this.#acquirers.clear();
  }

  /** Dispose of the resources of this cache entry */
  dispose(): void {
    if (this.response) {
      safeDispose(this.response);
      this.response = undefined;
    }
    // If an unreferenced entry has already scheduled its timeout and
    // is then disposed by reload, the old timer can still fire later and emit
    // 'disposed' again; its original listener deletes this key from the cache,
    // which can remove the replacement entry that reload just installed.
    // Therefore the dispose timeout is cleared here before emitting 'disposed'.
    window.clearTimeout(this.disposeTimeout);
    this.disposed.emit();
  }

  /** Schedule the disposal of this element after the lifespan expired */
  scheduleDisposeIfNeeded(): void {
    if (this.#acquirers.size > 0 || !this.promiseSettled) return;

    this.disposeTimeout = window.setTimeout(() => {
      this.dispose();
    }, this.lifespan);
  }

  /** Clear the dispose timeout if it's running */
  dismissDispose(): void {
    window.clearTimeout(this.disposeTimeout);
    this.disposeTimeout = undefined;
  }

  /** @returns whether the promise was settled */
  get promiseSettled(): boolean {
    return this.#promiseSettled;
  }

  /** @returns whether this cache entry is currently referenced by any acquirers */
  get isReferenced(): boolean {
    return this.#acquirers.size > 0;
  }

  /**
   * @returns Whether the given acquirer is holding this entry
   * @param acquirer The acquirer to check among this entry's acquirers
   */
  isReferencedBy(acquirer: ObjectCacheAcquirer): boolean {
    return this.#acquirers.has(acquirer);
  }

  /** @returns the list of owners that are acquiring this cache entry right now */
  get acquirers(): ObjectCacheAcquirers {
    return Array.from(this.#acquirers);
  }
}
